/**
 * Bubble for a Note that arrived via the feed.
 *
 * - text source → no "voice / video" label, just the body.
 * - non-text sources → show a small label so the user knows the source.
 * - transcript missing → "Processing…" while the worker is still busy.
 */
import { Link } from "react-router-dom";
import { AssistantBubble } from "./AssistantBubble";
import { Markdown } from "../../components/Markdown";
import { RelativeTime } from "../../components/RelativeTime";
import type { CapturedNoteMessage } from "../types";

const SOURCE_LABEL: Record<CapturedNoteMessage["sourceType"], string | null> = {
  voice: "Voice note",
  video_note: "Video circle",
  text: null,
  forward: "Forwarded",
  pdf: "PDF",
  docx: "Document",
  photo: "Photo",
};

interface Props {
  message: CapturedNoteMessage;
}

export function CapturedNoteBubble({ message }: Props): React.ReactElement {
  const isProcessing =
    message.transcript === null && message.sourceType !== "text";
  const body = message.transcript ?? message.rawPayload;
  const title = SOURCE_LABEL[message.sourceType];
  return (
    <AssistantBubble
      title={title ?? undefined}
      meta={
        <span className="inline-flex items-center gap-1.5">
          <RelativeTime iso={message.createdAt} />
          {message.projectId ? (
            <>
              <span aria-hidden="true">·</span>
              <Link
                to={`/projects/${encodeURIComponent(message.projectId)}`}
                className="hover:underline"
                style={{ color: "var(--color-link)" }}
              >
                Open project
              </Link>
            </>
          ) : null}
        </span>
      }
    >
      {isProcessing ? (
        <span
          className="italic aim-shimmer"
          style={{ color: "var(--color-hint)" }}
        >
          Processing…
        </span>
      ) : message.sourceType === "text" ? (
        // The user's own typed text — don't try to interpret Markdown
        // in it; render verbatim with whitespace preserved.
        <span className="whitespace-pre-wrap">{body}</span>
      ) : (
        // AI-produced transcript (voice / video / photo OCR) may
        // include the model's own emphasis when summarising. Render
        // through the same Markdown pipeline as ask answers.
        <Markdown text={body} />
      )}
    </AssistantBubble>
  );
}
